// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useState } from "react";
import "./UserEventsTable.scss";
import { useTranslation } from "react-i18next";
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';
import { format } from "date-fns";

export default function UserEventsTable({ logs, users, getUserDisplayName }) {
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState(10);

    // Group logs by user and source
    const groupedData = useMemo(() => {
        const result = [];
        const groupedByUser = {};

        // Group by user first
        logs.forEach(log => {
            if (!groupedByUser[log.user]) {
                groupedByUser[log.user] = [];
            }
            groupedByUser[log.user].push(log);
        });

        // Then for each user, group by source
        Object.entries(groupedByUser).forEach(([userId, userLogs]) => {
            const groupedBySource = {};
            
            userLogs.forEach(log => {
                if (!groupedBySource[log.source]) {
                    groupedBySource[log.source] = [];
                }
                groupedBySource[log.source].push(log);
            });

            // Create table data
            const user = {
                userId,
                userName: getUserDisplayName(userId),
                totalEvents: userLogs.length,
                sources: Object.entries(groupedBySource).map(([source, sourceLogs]) => ({
                    source,
                    events: sourceLogs.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate())
                }))
            };
            
            result.push(user);
        });

        // Sort by most active users
        return result.sort((a, b) => b.totalEvents - a.totalEvents);
    }, [logs, getUserDisplayName]);

    const columns = useMemo(() => [
        {
            Header: t('user') || 'User',
            accessor: 'userName',
            width: 200,
        },
        {
            Header: t('total_events') || 'Total Events',
            accessor: 'totalEvents',
            width: 150,
            Cell: ({ value }) => (
                <div className="event-count">
                    <span className="count-badge">{value}</span>
                </div>
            )
        },
        {
            Header: t('sources') || 'Sources',
            id: 'sources',
            Cell: ({ row }) => (
                <div className="sources-container">
                    {row.original.sources.map(sourceData => (
                        <span 
                            key={sourceData.source} 
                            className={`source-badge ${sourceData.source}`}
                        >
                            {sourceData.source} ({sourceData.events.length})
                        </span>
                    ))}
                </div>
            )
        },
    ], [t]);

    const renderRowSubComponent = React.useCallback(({ row }) => {
        return (
            <div className="user-events-detail">
                {row.original.sources.map(sourceData => (
                    <div key={sourceData.source} className="source-events">
                        <h4>{sourceData.source}</h4>
                        <table className="events-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Event</th>
                                    <th>Parameters</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sourceData.events.map((event, idx) => (
                                    <tr key={idx}>
                                        <td className="time-cell">{format(event.timestamp.toDate(), "MMM dd, yyyy HH:mm:ss")}</td>
                                        <td className="event-name-cell">{event.event_name}</td>
                                        <td className="params-cell">
                                            {event.event_params && Object.keys(event.event_params).length > 0 ? (
                                                <div className="params-grid">
                                                    {Object.entries(event.event_params).map(([key, value]) => (
                                                        <div key={key} className="param-item">
                                                            <span className="param-key">{key}:</span>
                                                            <span className="param-value">
                                                                {typeof value === 'object' 
                                                                    ? JSON.stringify(value) 
                                                                    : String(value)
                                                                }
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="no-params">No parameters</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        );
    }, []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        visibleColumns,
        state: { expanded, pageIndex },
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize: setTablePageSize,
    } = useTable(
        { 
            columns, 
            data: groupedData,
            initialState: {
                sortBy: [{ id: 'totalEvents', desc: true }],
                pageIndex: 0,
                pageSize
            },
            autoResetExpanded: false
        },
        useSortBy,
        useExpanded,
        usePagination
    );

    const handlePageSizeChange = (e) => {
        const newPageSize = Number(e.target.value);
        setPageSize(newPageSize);
        setTablePageSize(newPageSize);
    };

    if (groupedData.length === 0) {
        return (
            <div className="user-events-table empty-state">
                <h3>{t('user_events') || 'User Events'}</h3>
                <p>{t('no_events_found') || 'No events found for the selected filters.'}</p>
            </div>
        );
    }

    return (
        <div className="user-events-table">
            <h3>{t('user_events') || 'User Events'}</h3>
            <div className="table-wrapper">
                <table {...getTableProps()}>
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                <th className="expander-column"></th>
                                {headerGroup.headers.map(column => (
                                    <th 
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className={column.isSorted ? (column.isSortedDesc ? 'sorted-desc' : 'sorted-asc') : ''}
                                    >
                                        {column.render('Header')}
                                        <span className="sort-indicator">
                                            {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map(row => {
                            prepareRow(row);
                            const isExpanded = expanded[row.id];
                            return (
                                <React.Fragment key={row.getRowProps().key}>
                                    <tr {...row.getRowProps()} className={isExpanded ? 'row-expanded' : ''}>
                                        <td className="expander-column">
                                            <button 
                                                {...row.getToggleRowExpandedProps()}
                                                className={`expander-button ${isExpanded ? 'expanded' : 'collapsed'}`}
                                            >
                                                {isExpanded ? '▼' : '▶'}
                                            </button>
                                        </td>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                    {isExpanded && (
                                        <tr className="expanded-row">
                                            <td colSpan={visibleColumns.length + 1}>
                                                {renderRowSubComponent({ row })}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            
            <div className="pagination">
                <div className="pagination-controls">
                    <button 
                        onClick={() => gotoPage(0)} 
                        disabled={!canPreviousPage}
                        className="pagination-button"
                    >
                        {'<<'}
                    </button>
                    <button 
                        onClick={() => previousPage()} 
                        disabled={!canPreviousPage}
                        className="pagination-button"
                    >
                        {'<'}
                    </button>
                    <span className="pagination-info">
                        {t('page') || 'Page'}{' '}
                        <strong>
                            {pageIndex + 1} {t('of') || 'of'} {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <button 
                        onClick={() => nextPage()} 
                        disabled={!canNextPage}
                        className="pagination-button"
                    >
                        {'>'}
                    </button>
                    <button 
                        onClick={() => gotoPage(pageCount - 1)} 
                        disabled={!canNextPage}
                        className="pagination-button"
                    >
                        {'>>'}
                    </button>
                </div>
                <div className="page-size-selector">
                    <select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="page-size-select"
                    >
                        {[10, 20, 30, 40, 50].map(size => (
                            <option key={size} value={size}>
                                {t('show') || 'Show'} {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
} 